import type {
  TermsOfTransition,
  TermsOfTransitionAction,
} from 'types/course/termsOfTransition';

const passingActions: Partial<TermsOfTransitionAction>[] = [
  'Go_To_Next',
  'End_All',
  'Skip',
];

/** Actions that move the learner forward rather than back onto the same content. */
export function isPassingTransitionAction(
  action?: TermsOfTransitionAction | null,
): boolean {
  return !!action && passingActions.includes(action);
}

function distanceToBand(term: TermsOfTransition, score: number): number {
  if (score < term.from) return term.from - score;
  if (score > term.to) return score - term.to;
  return 0;
}

export function useTermsOfTransition(termsOfTransition: TermsOfTransition[]) {
  function getTermsOfTransition(score: number) {
    if (!Array.isArray(termsOfTransition) || !termsOfTransition.length)
      return null;

    const matched = termsOfTransition.find(
      (t) => t.from <= score && t.to >= score,
    );
    if (matched) return matched;

    // CMS bands can leave the score uncovered: a section average of 30.67
    // falls between 0-30 and 31-100, and an author can simply mistype a range.
    // Returning nothing left the learner on a banner whose button did nothing,
    // so resolve the nearest band instead. Ties go to the stricter (lower)
    // band — never hand out a pass the score did not reach.
    return termsOfTransition.reduce((closest, term) => {
      const distance = distanceToBand(term, score);
      const closestDistance = distanceToBand(closest, score);
      if (distance < closestDistance) return term;
      if (distance === closestDistance && term.from < closest.from) return term;
      return closest;
    });
  }

  function isPassed(score: number) {
    const termsOfTransition = getTermsOfTransition(score);
    if (!termsOfTransition) return false;
    return isPassingTransitionAction(termsOfTransition.action);
  }

  return {
    termsOfTransition,
    getTermsOfTransition,
    isPassed,
  };
}
